// AuditPerformanceMonitor - Handles performance monitoring and health checks
export class AuditPerformanceMonitor {
  constructor(auditService) {
    this.auditService = auditService;
    this.metrics = {
      apiCalls: 0,
      successfulCalls: 0,
      failedCalls: 0,
      averageResponseTime: 0,
      lastHealthCheck: null
    };
    this.performanceLog = [];
  }

  // Get detailed performance metrics
  getPerformanceMetrics() {
    const queueSize = this.auditService.saveQueue.size;
    const activeTimestamps = this.auditService.lastSaveTimestamp.size;
    const currentSession = this.auditService.sessionManager.getCurrentSessionId();
    const totalSessions = this.auditService.sessionManager.getAllStoredSessions().length;
    
    // Calculate storage usage
    let storageUsed = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('auditResponse_')) {
        storageUsed += localStorage.getItem(key).length;
      }
    }
    
    return {
      queueSize,
      activeTimestamps,
      currentSession,
      totalSessions,
      storageUsedBytes: storageUsed,
      storageUsedKB: Math.round(storageUsed / 1024),
      storageUsedMB: Math.round(storageUsed / (1024 * 1024)),
      isIndependentMode: this.auditService.independentMode,
      apiMetrics: { ...this.metrics },
      memoryUsage: this.getMemoryUsage(),
      timestamp: new Date().toISOString()
    };
  }

  // Get memory usage estimates
  getMemoryUsage() {
    const sessions = this.auditService.sessionManager.getAllStoredSessions();
    let estimatedMemoryKB = 0;
    
    // Estimate memory usage from stored sessions
    sessions.forEach(session => {
      const sessionString = JSON.stringify(session.auditResponse);
      estimatedMemoryKB += Math.round(sessionString.length / 1024);
    });
    
    return {
      estimatedSessionMemoryKB: estimatedMemoryKB,
      queueMemoryKB: Math.round(JSON.stringify(Array.from(this.auditService.saveQueue.entries())).length / 1024),
      totalEstimatedKB: estimatedMemoryKB + Math.round(JSON.stringify(Array.from(this.auditService.saveQueue.entries())).length / 1024)
    };
  }

  // Record API call metrics
  recordApiCall(success, responseTime) {
    this.metrics.apiCalls++;
    if (success) {
      this.metrics.successfulCalls++;
    } else {
      this.metrics.failedCalls++;
    }
    
    // Update average response time
    this.metrics.averageResponseTime = (
      (this.metrics.averageResponseTime * (this.metrics.apiCalls - 1) + responseTime) / 
      this.metrics.apiCalls
    );
    
    // Log performance data
    this.performanceLog.push({
      timestamp: Date.now(),
      success,
      responseTime,
      type: 'api_call'
    });
    
    // Keep only last 100 entries
    if (this.performanceLog.length > 100) {
      this.performanceLog = this.performanceLog.slice(-100);
    }
  }

  // Comprehensive health check
  async healthCheck() {
    const startTime = Date.now();
    const metrics = this.getPerformanceMetrics();
    const issues = [];
    const warnings = [];
    
    console.log('🏥 Starting comprehensive health check...');
    
    // Check for issues
    if (metrics.queueSize > 50) {
      issues.push(`High queue size: ${metrics.queueSize} pending saves`);
    }
    
    if (metrics.storageUsedKB > 5000) {
      warnings.push(`High storage usage: ${metrics.storageUsedKB}KB`);
    }
    
    if (metrics.totalSessions > 10) {
      warnings.push(`Many stored sessions: ${metrics.totalSessions}`);
    }
    
    // Check API connectivity if current audit exists
    let apiConnectivity = 'unknown';
    if (this.auditService.currentAuditResponse) {
      try {
        const apiStartTime = Date.now();
        const result = await this.auditService.getAuditResponse(
          this.auditService.currentAuditResponse.audit_form_response_id,
          { useCache: false }
        );
        const apiResponseTime = Date.now() - apiStartTime;
        
        this.recordApiCall(result.success, apiResponseTime);
        apiConnectivity = result.success ? 'healthy' : 'error';
        
        if (!result.success) {
          issues.push(`API connectivity issue: ${result.error}`);
        }
      } catch (error) {
        this.recordApiCall(false, Date.now() - startTime);
        apiConnectivity = 'error';
        issues.push(`API connectivity issue: ${error.message}`);
      }
    }
    
    // Check session integrity
    const sessionCheck = this.checkSessionIntegrity();
    if (sessionCheck.corruptedSessions > 0) {
      issues.push(`${sessionCheck.corruptedSessions} corrupted sessions found`);
    }
    
    // Check performance metrics
    if (this.metrics.failedCalls > this.metrics.successfulCalls) {
      issues.push('More API failures than successes');
    }
    
    if (this.metrics.averageResponseTime > 5000) {
      warnings.push(`Slow API response time: ${Math.round(this.metrics.averageResponseTime)}ms`);
    }
    
    const status = issues.length === 0 ? 'healthy' : 'issues';
    const checkDuration = Date.now() - startTime;
    
    const healthReport = {
      status,
      apiConnectivity,
      metrics,
      sessionIntegrity: sessionCheck,
      issues,
      warnings,
      recommendations: this.generateHealthRecommendations(issues, warnings, metrics),
      checkDuration,
      checkedAt: new Date().toISOString()
    };
    
    this.metrics.lastHealthCheck = healthReport;
    
    console.log(`🏥 Health check completed in ${checkDuration}ms: ${status}`);
    
    return healthReport;
  }

  // Check session integrity
  checkSessionIntegrity() {
    const sessions = this.auditService.sessionManager.getAllStoredSessions();
    let validSessions = 0;
    let corruptedSessions = 0;
    const issues = [];
    
    sessions.forEach(({ sessionId, auditResponse }) => {
      try {
        // Basic validation
        if (!auditResponse.audit_form_response_id) {
          issues.push(`Session ${sessionId}: Missing audit_form_response_id`);
          corruptedSessions++;
          return;
        }
        
        if (!auditResponse.status) {
          issues.push(`Session ${sessionId}: Missing status`);
          corruptedSessions++;
          return;
        }
        
        // Check for required structure
        if (typeof auditResponse !== 'object') {
          issues.push(`Session ${sessionId}: Invalid data structure`);
          corruptedSessions++;
          return;
        }
        
        validSessions++;
      } catch (error) {
        issues.push(`Session ${sessionId}: Validation error - ${error.message}`);
        corruptedSessions++;
      }
    });
    
    return {
      totalSessions: sessions.length,
      validSessions,
      corruptedSessions,
      issues,
      integrityScore: sessions.length > 0 ? Math.round((validSessions / sessions.length) * 100) : 100
    };
  }

  // Generate health recommendations
  generateHealthRecommendations(issues, warnings, metrics) {
    const recommendations = [];
    
    if (metrics.queueSize > 20) {
      recommendations.push({
        priority: 'high',
        action: 'Clear pending saves',
        description: 'Call forceSaveAll() to clear pending saves and improve performance'
      });
    }
    
    if (metrics.storageUsedKB > 3000) {
      recommendations.push({
        priority: 'medium',
        action: 'Clean up storage',
        description: 'Call cleanupOldSessions() to free storage space'
      });
    }
    
    if (metrics.totalSessions > 5) {
      recommendations.push({
        priority: 'low',
        action: 'Session cleanup',
        description: 'Clean up old completed audit sessions to improve performance'
      });
    }
    
    if (this.metrics.failedCalls > 5) {
      recommendations.push({
        priority: 'high',
        action: 'Check network connectivity',
        description: 'Multiple API failures detected. Check network connection and server status'
      });
    }
    
    if (issues.length > 0) {
      recommendations.push({
        priority: 'critical',
        action: 'Address critical issues',
        description: 'Resolve critical issues before continuing audit operations'
      });
    }
    
    if (recommendations.length === 0) {
      recommendations.push({
        priority: 'info',
        action: 'System healthy',
        description: 'System is operating normally'
      });
    }
    
    return recommendations;
  }

  // Performance optimization suggestions
  getOptimizationSuggestions() {
    const metrics = this.getPerformanceMetrics();
    const suggestions = [];
    
    // Storage optimization
    if (metrics.storageUsedKB > 1000) {
      suggestions.push({
        category: 'storage',
        suggestion: 'Consider implementing data compression for stored sessions',
        impact: 'medium',
        effort: 'high'
      });
    }
    
    if (metrics.totalSessions > 20) {
      suggestions.push({
        category: 'storage',
        suggestion: 'Implement automatic cleanup of old completed sessions',
        impact: 'high',
        effort: 'low'
      });
    }
    
    // Performance optimization
    if (metrics.queueSize > 10) {
      suggestions.push({
        category: 'performance',
        suggestion: 'Reduce save queue debounce time for faster saves',
        impact: 'medium',
        effort: 'low'
      });
    }
    
    if (this.metrics.averageResponseTime > 3000) {
      suggestions.push({
        category: 'performance',
        suggestion: 'Implement request caching to reduce API calls',
        impact: 'high',
        effort: 'medium'
      });
    }
    
    // User experience optimization
    if (metrics.totalSessions > 5) {
      suggestions.push({
        category: 'ux',
        suggestion: 'Add session management UI for better user control',
        impact: 'medium',
        effort: 'high'
      });
    }
    
    return suggestions;
  }

  // Monitor performance over time
  startPerformanceMonitoring(intervalMs = 60000) {
    console.log(`📊 Starting performance monitoring (${intervalMs}ms intervals)`);
    
    const monitoringInterval = setInterval(async () => {
      try {
        const metrics = this.getPerformanceMetrics();
        
        // Log significant changes
        if (metrics.queueSize > 20) {
          console.warn(`⚠️ High queue size detected: ${metrics.queueSize}`);
        }
        
        if (metrics.storageUsedKB > 2000) {
          console.warn(`⚠️ High storage usage detected: ${metrics.storageUsedKB}KB`);
        }
        
        // Auto-cleanup if needed
        if (metrics.totalSessions > 15) {
          console.log('🧹 Auto-cleanup triggered for old sessions');
          this.auditService.sessionManager.cleanupOldSessions();
        }
        
        // Store monitoring data
        this.performanceLog.push({
          timestamp: Date.now(),
          type: 'monitoring',
          metrics: {
            queueSize: metrics.queueSize,
            storageUsedKB: metrics.storageUsedKB,
            totalSessions: metrics.totalSessions
          }
        });
        
      } catch (error) {
        console.error('❌ Performance monitoring error:', error);
      }
    }, intervalMs);
    
    // Return cleanup function
    return () => {
      clearInterval(monitoringInterval);
      console.log('📊 Performance monitoring stopped');
    };
  }

  // Get performance trends
  getPerformanceTrends(timeRangeMs = 3600000) { // 1 hour default
    const cutoffTime = Date.now() - timeRangeMs;
    const recentLogs = this.performanceLog.filter(log => log.timestamp > cutoffTime);
    
    if (recentLogs.length === 0) {
      return {
        message: 'No recent performance data available',
        timeRange: timeRangeMs,
        dataPoints: 0
      };
    }
    
    const apiCalls = recentLogs.filter(log => log.type === 'api_call');
    const monitoring = recentLogs.filter(log => log.type === 'monitoring');
    
    // Calculate trends
    const trends = {
      timeRange: timeRangeMs,
      dataPoints: recentLogs.length,
      apiPerformance: {
        totalCalls: apiCalls.length,
        successRate: apiCalls.length > 0 ? 
          (apiCalls.filter(call => call.success).length / apiCalls.length * 100) : 0,
        averageResponseTime: apiCalls.length > 0 ? 
          apiCalls.reduce((sum, call) => sum + call.responseTime, 0) / apiCalls.length : 0
      },
      systemTrends: {
        averageQueueSize: monitoring.length > 0 ? 
          monitoring.reduce((sum, log) => sum + (log.metrics?.queueSize || 0), 0) / monitoring.length : 0,
        averageStorageKB: monitoring.length > 0 ? 
          monitoring.reduce((sum, log) => sum + (log.metrics?.storageUsedKB || 0), 0) / monitoring.length : 0,
        averageSessionCount: monitoring.length > 0 ? 
          monitoring.reduce((sum, log) => sum + (log.metrics?.totalSessions || 0), 0) / monitoring.length : 0
      }
    };
    
    return trends;
  }

  // Benchmark performance
  async benchmarkPerformance() {
    console.log('🏃 Starting performance benchmark...');
    const benchmark = {
      timestamp: new Date().toISOString(),
      tests: []
    };
    
    // Test 1: Storage read/write performance
    const storageStart = Date.now();
    const testData = { test: 'data', timestamp: Date.now(), data: new Array(1000).fill('test') };
    localStorage.setItem('benchmark_test', JSON.stringify(testData));
    const retrieved = JSON.parse(localStorage.getItem('benchmark_test'));
    localStorage.removeItem('benchmark_test');
    const storageTime = Date.now() - storageStart;
    
    benchmark.tests.push({
      name: 'Storage Read/Write',
      duration: storageTime,
      success: retrieved.test === 'data',
      details: `Stored and retrieved ${JSON.stringify(testData).length} bytes`
    });
    
    // Test 2: Session management performance
    const sessionStart = Date.now();
    const sessions = this.auditService.sessionManager.getAllStoredSessions();
    const sessionAnalysis = sessions.map(s => this.auditService.sessionManager.getSessionSummary(s.sessionId));
    const sessionTime = Date.now() - sessionStart;
    
    benchmark.tests.push({
      name: 'Session Management',
      duration: sessionTime,
      success: true,
      details: `Processed ${sessions.length} sessions`
    });
    
    // Test 3: Data transformation performance
    const transformStart = Date.now();
    const sampleData = {
      '1': {
        categoryName: 'Test Category',
        controlItems: {
          '1': {
            controlItemName: 'Test Control Item',
            checks: {
              '1': { isChecked: true, comment: 'Test failure' },
              '2': { isChecked: false, comment: 'Test pass' }
            }
          }
        }
      }
    };
    const sampleFormData = { hierarchy: { checks: [], categories: [], control_items: [] } };
    const transformResult = this.auditService.transformer.transformCumulativeAuditDataToFieldResponses(
      sampleData, 
      { data: sampleFormData, hierarchy: sampleFormData }
    );
    const transformTime = Date.now() - transformStart;
    
    benchmark.tests.push({
      name: 'Data Transformation',
      duration: transformTime,
      success: Array.isArray(transformResult),
      details: `Transformed data with ${transformResult.length} field responses`
    });
    
    const totalTime = Date.now() - Date.parse(benchmark.timestamp);
    benchmark.totalDuration = totalTime;
    benchmark.summary = {
      totalTests: benchmark.tests.length,
      passedTests: benchmark.tests.filter(t => t.success).length,
      averageDuration: benchmark.tests.reduce((sum, t) => sum + t.duration, 0) / benchmark.tests.length
    };
    
    console.log(`🏃 Benchmark completed in ${totalTime}ms`);
    console.log(`✅ ${benchmark.summary.passedTests}/${benchmark.summary.totalTests} tests passed`);
    
    return benchmark;
  }

  // Reset performance monitoring
  reset() {
    this.metrics = {
      apiCalls: 0,
      successfulCalls: 0,
      failedCalls: 0,
      averageResponseTime: 0,
      lastHealthCheck: null
    };
    this.performanceLog = [];
    console.log('📊 Performance monitor reset');
  }

  // Export performance data
  exportPerformanceData() {
    return {
      exportedAt: new Date().toISOString(),
      metrics: this.metrics,
      performanceLog: this.performanceLog,
      currentMetrics: this.getPerformanceMetrics(),
      lastHealthCheck: this.metrics.lastHealthCheck
    };
  }
}